import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiBell, FiMail, FiFileText, FiCheckCircle } from 'react-icons/fi';
import ShieldCheckIcon from '../../ShieldCheckIcon';
import { toast } from '../../Toast';
import { BackLink, HubHeader, hubPath, useHubSite } from '../shared';
import { ContactFormSettings, TabProps, initialSettings } from './settings/formSettingsData';
import Notifications from './settings/Notifications';
import AutoReply from './settings/AutoReply';
import Questions from './settings/Questions';
import Consent from './settings/Consent';
import SpamProtection from './settings/SpamProtection';

// left-hand nav; the url segment after /website-hub/contact-forms/settings picks the tab
const TABS: { seg: string; label: string; hint: string; sub: string; icon: React.ReactNode; tone: string; page: React.FC<TabProps> }[] = [
    { seg: '', label: 'Notifications', hint: 'Who is told about messages', sub: 'Who is told when a message arrives', icon: <FiBell />, tone: 'navy', page: Notifications },
    { seg: 'automatic-reply', label: 'Automatic Reply', hint: 'What the sender gets back', sub: 'What the sender gets back, straight away', icon: <FiMail />, tone: 'cyan', page: AutoReply },
    { seg: 'questions', label: 'Questions on the Form', hint: 'Fields, order and required', sub: 'Which questions the form asks, and in what order', icon: <FiFileText />, tone: 'grey', page: Questions },
    { seg: 'consent', label: 'Consent & After Send', hint: 'Opt-in and the thank you', sub: 'The tick-box under the form and what happens next', icon: <FiCheckCircle />, tone: 'green', page: Consent },
    { seg: 'spam', label: 'Spam Protection', hint: 'Filters, blocking and review', sub: 'Filters, blocking and the review folder', icon: <ShieldCheckIcon />, tone: 'cyan', page: SpamProtection },
];

// Website Hub › Contact forms › Form settings (spec 410:12432, 713:6606, 713:6230, 713:5854, 713:5478).
// One form for all five tabs, so Save / Cancel cover everything and switching tabs keeps unsaved edits.
const FormSettings: React.FC<{ tab?: string }> = ({ tab }) => {
    const history = useHistory();
    const { host, short } = useHubSite();
    const current = TABS.find(x => x.seg === (tab ?? '')) ?? TABS[0];

    const [form, setFormState] = useState<ContactFormSettings>(() => initialSettings(host, short));
    const [saved, setSaved] = useState(form);
    const dirty = JSON.stringify(form) !== JSON.stringify(saved);
    const setForm = (update: (f: ContactFormSettings) => ContactFormSettings) => setFormState(update);

    // the client loads after the first render (and can be switched): start again from its sample settings
    useEffect(() => {
        const s = initialSettings(host, short);
        setFormState(s);
        setSaved(s);
    }, [host, short]);

    const save = () => {
        setSaved(form);
        toast.info('Preview only: contact form settings aren’t saved yet');
    };
    const cancel = () => {
        setFormState(saved);
        history.push(hubPath('contact-forms'));
    };

    // tablet / mobile: the nav is a sideways tab row, so bring the active item into view
    const navRef = useRef<HTMLElement>(null);
    useEffect(() => {
        const nav = navRef.current;
        const active = nav?.querySelector<HTMLElement>('button.active');
        if (!nav || !active || nav.scrollWidth <= nav.clientWidth) return;
        nav.scrollLeft = active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2;
    }, [current]);

    const actions = <>
        <button type="button" className="hub-fs-cancel" onClick={cancel}>Cancel</button>
        <button type="button" className="btn-primary" onClick={save} disabled={!dirty}>Save Settings</button>
    </>;

    return <>
        <BackLink to={hubPath('contact-forms')} label="Back to Contact forms" />
        <HubHeader title="Contact Form Settings" sub={`${current.label} · ${current.sub}`}>{actions}</HubHeader>

        <div className="ann-row">
            <nav className="ann-nav hub-fs-nav" ref={navRef}>
                <div className="nav-title">Form settings</div>
                {TABS.map(x => <button key={x.seg} type="button" className={x === current ? 'active' : ''}
                    onClick={() => history.push(hubPath('contact-forms', 'settings', x.seg))}>
                    <span className={`hub-fs-nav-icon tone-${x.tone}`}>{x.icon}</span>
                    <span className="text"><strong>{x.label}</strong><small>{x.hint}</small></span>
                </button>)}
            </nav>

            <div className="cs-main">
                <current.page form={form} setForm={setForm} />
            </div>
        </div>

        <div className="action-bottom hub-fs-actions">{actions}</div>
    </>;
};

export default FormSettings;
